// Phase 23B.5 — SE mobile outlets list (with pending-sync badge)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Store, MapPin, CloudUpload, ArrowLeft, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { listAll as listAllOutbox, STATUS } from "@/offline/outbox";
import { drainQueue } from "@/offline/sync";
import { isOnline, getSimulateOffline } from "@/offline/apiClient";
import PermissionGate, { useHasPerm } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import OutletColorChip from "@/components/outlets/OutletColorChip";
import OutletColorLegend from "@/components/outlets/OutletColorLegend";
import {
  activeColourIn, countByColour, matchesColour, useOutletColors,
} from "@/lib/outletColors";


export default function OutletsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canAdd = useHasPerm("ADD_OUTLET");

  const [outlets, setOutlets] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [colourFilter, setColourFilter] = useState(null); // outlet colour-guide filter

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/retailers", { params: { page_size: 100 } });
      setOutlets(r.data.items || []);
      // Pull pending outlet creations from the outbox
      const all = await listAllOutbox();
      const pendingOutlets = all.filter(row =>
        row.path === "/retailers"
        && row.method === "POST"
        && row.status !== STATUS.SUCCESS
        && row.status !== STATUS.DISCARDED
      );
      setPending(pendingOutlets);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Re-drain on network return
  useEffect(() => {
    const onOnline = () => { drainQueue().then(load).catch(() => {}); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      await drainQueue();
      toast.success("Sync complete");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const filtered = outlets.filter(o =>
    !q ||
    (o.code || "").toLowerCase().includes(q.toLowerCase()) ||
    (o.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (o.phone || "").includes(q)
  );

  // Outlet colour coding — colours for every loaded outlet (not just the
  // searched ones, so typing in Search doesn't refetch). Guide counts follow
  // the search; the colour filter applies on top.
  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const { colors, legend } = useOutletColors(outletIds);
  const activeColour = activeColourIn(legend, colourFilter);
  const colourCounts = countByColour(filtered, (o) => colors[o.id]);
  const shown = activeColour
    ? filtered.filter((o) => matchesColour(colors[o.id], activeColour)) : filtered;

  return (
    <div className="p-3 space-y-3 pb-20" data-testid="outlets-list-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")}
                 data-testid="outlets-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
            <Store className="h-5 w-5 text-[#6D2C91]" /> My Outlets
          </h1>
          <p className="text-[11px] text-neutral-500">
            {outlets.length} outlets · {pending.length > 0 ? `${pending.length} pending sync` : "all synced"}
          </p>
        </div>
        {pending.length > 0 && isOnline() && !getSimulateOffline() && (
          <Button size="sm" variant="outline" onClick={doSync} disabled={syncing}
                  data-testid="outlets-sync-btn">
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search…" className="h-9"
              data-testid="outlets-search" />

      <OutletColorLegend legend={legend} counts={colourCounts}
                         active={activeColour} onChange={setColourFilter}
                         testid="outlets-color-legend" />

      {/* Pending sync cards */}
      {pending.map(row => {
        const inFlight = row.status === STATUS.IN_FLIGHT;
        return (
        <Card key={row._id || row.idempotency_key} className="border-amber-300 bg-amber-50/50 rounded-2xl"
              data-testid={`outlet-pending-${row.idempotency_key}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 text-amber-700 p-1.5">
              {inFlight
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <CloudUpload className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-neutral-900 truncate">
                {row.body?.name || "(unnamed draft)"}
              </div>
              <div className="text-[11px] text-neutral-600 truncate">
                {row.body?.address}
              </div>
              {row.last_error && (
                <div className="text-[10px] text-rose-600 mt-0.5">Sync error: {row.last_error}</div>
              )}
            </div>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]"
                    data-testid="outlet-pending-badge">
              {inFlight ? "Syncing…" : "Pending sync"}
            </Badge>
            <Button
              size="icon" variant="ghost"
              className="h-8 w-8"
              disabled={inFlight}
              onClick={() => navigate(`/outlets/new?draft=${row._id}`)}
              data-testid={`outlet-pending-edit-${row.idempotency_key}`}
              title={inFlight ? "Syncing — cannot edit" : "Edit draft"}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      );})}

      {/* Synced outlets */}
      {loading && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>}
      {!loading && filtered.length === 0 && pending.length === 0 && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="py-12 text-center text-sm text-neutral-400">No outlets yet.</CardContent>
        </Card>
      )}
      {!loading && activeColour && filtered.length > 0 && shown.length === 0 && (
        <div className="py-8 text-center text-sm text-neutral-400" data-testid="outlets-color-empty">
          No outlet with this colour here.
        </div>
      )}
      {!loading && shown.map(o => (
        <Card key={o.id}
              className={"border-neutral-200 rounded-2xl" + (colors[o.id] ? " border-l-4" : "")}
              style={colors[o.id] ? { borderLeftColor: colors[o.id].color } : undefined}
              data-testid={`outlet-row-${o.id}`}>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="rounded-lg bg-[#6D2C91]/10 text-[#6D2C91] p-1.5">
              <Store className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-neutral-900 truncate">{o.name}</div>
              {colors[o.id] && (
                <div className="mt-0.5">
                  <OutletColorChip tag={colors[o.id]} testid={`outlet-color-${o.id}`} />
                </div>
              )}
              <div className="text-[11px] text-neutral-500 flex items-center gap-1 truncate">
                <span className="font-mono">{o.code}</span>
                {o.gps_lat && <><MapPin className="h-2.5 w-2.5" /><span>geo-tagged</span></>}
              </div>
            </div>
            {o.storefront_photo_ref && (
              <Badge className="bg-emerald-100 text-emerald-800 text-[9px] border-emerald-200">📷</Badge>
            )}
          </CardContent>
        </Card>
      ))}

      {/* FAB */}
      <PermissionGate perm="ADD_OUTLET">
        <button
          onClick={() => navigate("/outlets/new")}
          className="fixed bottom-20 right-4 h-14 w-14 rounded-full bg-[#6D2C91] text-white shadow-lg
                     hover:bg-[#5b2478] flex items-center justify-center"
          data-testid="outlets-add-fab">
          <Plus className="h-6 w-6" />
        </button>
      </PermissionGate>
    </div>
  );
}
